import { Link } from '@tanstack/react-router';
import { Search, Sparkles } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { CompetitorAnalysisCreateButton } from '@/features/competitor-analyses/components/competitor-analysis-create-button';
import { useProjects } from '@/features/projects/use-projects';
import { useCompetitorAnalyses } from '@/features/competitor-analyses/use-competitor-analyses';
import { relTime, hostname } from '@/lib/format';
import { useProjectFilterParam } from '@/lib/use-project-filter';

// Shares the competitors page's key — both surfaces show the same data, so
// they must agree on the default project (they used to have separate keys
// and could silently disagree).
const PROJECT_STORAGE = 'seo:competitor-project';

export function CompetitorAnalysesRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];

  // Filter lives in the URL (?project=) — see useProjectFilterParam.
  const { projectId, setProjectId } = useProjectFilterParam({
    storageKey: PROJECT_STORAGE,
    projects: availableProjects,
    projectsLoading: projects.isLoading,
  });

  const analyses = useCompetitorAnalyses(projectId || undefined);
  const rows = analyses.data?.items ?? [];
  const activeProject = availableProjects.find((p) => p.id === projectId);

  return (
    <Page>
      <PageHeader
        title="Who ranks"
        description="Tear down the top results for one keyword you already want to win. For finding which keywords to chase, use Keywords."
        actions={
          <CompetitorAnalysisCreateButton
            projects={availableProjects}
            defaultProjectId={projectId}
          />
        }
      />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project">
            {availableProjects.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>You don't have any projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select
                value={projectId || '__all__'}
                onValueChange={(v) => setProjectId(v === '__all__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All projects</SelectItem>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          {activeProject ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {hostname(activeProject.domain)}
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 gap-2 text-center">
            <Surface tone="muted" className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Analyses
              </div>
              <div className="text-xl font-semibold tabular">{rows.length}</div>
            </Surface>
          </div>
        </Section>

        <div className="space-y-4">
          <Section>
            <SectionHeader>
              <div>
                <SectionTitle className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  Recent analyses
                </SectionTitle>
                <SectionDescription>
                  Each analysis reads the top-ranking pages and hands you a recommended outline.
                </SectionDescription>
              </div>
            </SectionHeader>

            {analyses.error ? (
              <ErrorBanner error={analyses.error} />
            ) : !projectId ? (
              <EmptyState
                icon={Search}
                title="Choose a project first"
                description="Pick a project on the left to see its keyword analyses."
                variant="inline"
              />
            ) : analyses.isLoading ? (
              <div className="flex justify-center p-6">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState
                icon={Search}
                title="Ready to research your next post?"
                description="Give us a keyword and we'll analyze the top 10 results in a couple of minutes."
                variant="inline"
                action={
                  <CompetitorAnalysisCreateButton
                    projects={availableProjects}
                    defaultProjectId={projectId}
                    buttonProps={{ size: 'sm' }}
                  />
                }
              />
            ) : (
              <ul className="space-y-2">
                {rows.map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/competitor-analyses/$analysisId"
                      params={{ analysisId: a.id }}
                      className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-sm truncate group-hover:text-primary">
                          {a.keyword}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {a.language.toUpperCase()} · {a.country.toUpperCase()} · {relTime(a.created_at)}
                        </div>
                      </div>
                      <StatusPill status={a.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </Page>
  );
}
